import { type MutableRefObject, type RefObject, useCallback, useEffect, useRef } from 'react';
import type { buttonUIFactory, popupUIFactory, Widget } from '@yandex-int/messenger.widget';
import { ASSISTANT_REQUEST_CLOSE_EVENT, type MessengerOpenDetail } from '../../lib/messenger';

type MessengerUi = ReturnType<typeof buttonUIFactory> | ReturnType<typeof popupUIFactory>;

interface UseSupportChatMountParams {
    ui: MessengerUi | null;
    headless: boolean;
    widgetRef: RefObject<Widget | null>;
    guidRef: RefObject<string>;
    pendingOpenRef: MutableRefObject<MessengerOpenDetail | null | undefined>;
}

export function useSupportChatMount({ ui, headless, widgetRef, guidRef, pendingOpenRef }: UseSupportChatMountParams) {
    const uiRef = useRef(ui);
    const mountTargetRef = useRef<HTMLElement | null>(null);
    const mountedUiRef = useRef<MessengerUi | null>(null);
    const isReadyRef = useRef(false);
    const prevUiRef = useRef<MessengerUi | null>(null);
    const prevHeadlessRef = useRef(headless);

    useEffect(() => {
        uiRef.current = ui;
    }, [ui]);

    const flushPendingOpen = useCallback(() => {
        if (pendingOpenRef.current === undefined || !widgetRef.current || !isReadyRef.current) {
            return;
        }

        const detail = pendingOpenRef.current;

        pendingOpenRef.current = undefined;

        widgetRef.current.show({
            guid: detail?.guid ?? guidRef.current,
            ...(detail ?? {}),
        });
        window.dispatchEvent(new Event(ASSISTANT_REQUEST_CLOSE_EVENT));
    }, [guidRef, pendingOpenRef, widgetRef]);

    const attemptMount = useCallback(() => {
        const currentUi = uiRef.current;

        if (!currentUi || mountedUiRef.current === currentUi) {
            return;
        }

        if (headless) {
            currentUi.mount();
        } else {
            if (!mountTargetRef.current) {
                return;
            }

            currentUi.mount(mountTargetRef.current);
        }

        mountedUiRef.current = currentUi;
        isReadyRef.current = true;
        flushPendingOpen();
    }, [flushPendingOpen, headless]);

    const registerMountTarget = useCallback(
        (element: HTMLElement | null) => {
            mountTargetRef.current = element;

            if (element) {
                attemptMount();
            }
        },
        [attemptMount]
    );

    useEffect(() => {
        if (prevUiRef.current !== ui || prevHeadlessRef.current !== headless) {
            mountedUiRef.current = null;
            isReadyRef.current = false;
            prevUiRef.current = ui;
            prevHeadlessRef.current = headless;
        }

        attemptMount();
    }, [ui, headless, attemptMount]);

    const showMessenger = useCallback(
        (detail?: MessengerOpenDetail) => {
            if (!widgetRef.current || !isReadyRef.current) {
                pendingOpenRef.current = detail ?? null;

                return;
            }

            widgetRef.current.show({
                guid: detail?.guid ?? guidRef.current,
                ...(detail ?? {}),
            });
            window.dispatchEvent(new Event(ASSISTANT_REQUEST_CLOSE_EVENT));
        },
        [guidRef, pendingOpenRef, widgetRef]
    );

    return {
        registerMountTarget,
        showMessenger,
    };
}
